'use client';

import classNames from 'classnames';
import { useParams, usePathname } from 'next/navigation';
import React, { FC, useEffect, useState } from 'react';

import { Route } from '@/app/[lang]/Router/createRoute';
import { PRODUCTS_ROUTE } from '@/app/[lang]/Router/routes';
import Image from '@/components/core/Image';
import { socialIcons } from '@/components/Layout/Footer';
import { STYLE_CLOSE, STYLE_OPEN } from '@/components/Layout/Header/MobileHeader/consts';
import LanguagesPage from '@/components/Layout/Header/MobileHeader/LangPage';
import SinglePage from '@/components/Layout/Header/MobileHeader/SinglePage';
import MobileMenuItemWrapper from '@/components/MobileMenuItem';
import { useDeviceSize } from '@/hooks/useDeviceSize';
import { useTranslation } from '@/hooks/useTranslations';
import { getCurrentLanguage } from '@/lib/i18n';

interface MobileHeaderProps {
  isOppened: boolean;
  pages: Route[];
  onClose: () => void;
}

const MobileHeader: FC<MobileHeaderProps> = ({ isOppened, pages, onClose }) => {
  const [style, setStyle] = useState<React.CSSProperties>(STYLE_OPEN);
  const [singlePage, setSinglePage] = useState<null | Route>(null);
  const [langPageIsOpen, setLangPageIsOpen] = useState<boolean>(false);

  const path = usePathname();
  const params = useParams();
  const { t } = useTranslation();
  const { isMobile } = useDeviceSize();

  const currentLang = getCurrentLanguage(params.lang as string);

  const selectPage = (page: Route, e?: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
    e?.preventDefault();
    e?.stopPropagation();
    setSinglePage(page);
  };

  useEffect(() => {
    if (singlePage || langPageIsOpen) {
      setStyle(STYLE_CLOSE);
    } else {
      setStyle(STYLE_OPEN);
    }
  }, [singlePage, langPageIsOpen]);

  return (
    <div
      className={classNames('menuBurgerMobile', {
        open: isOppened && isMobile,
      })}
    >
      <div className="menuBurgerMobContent">
        <div className="menuBurgerHomeInfoWrp">
          {!singlePage && !langPageIsOpen ? (
            <div className="menuBurgerHomeInfoInner" style={style}>
              {pages.map((page) => {
                const isGamesPage =
                  page.getPath(currentLang?.link as string) ===
                    PRODUCTS_ROUTE.getPath(currentLang?.link as string) &&
                  path.includes(PRODUCTS_ROUTE.getPath(currentLang?.link as string));
                return (
                  <MobileMenuItemWrapper
                    isNotUrl={!!page.isNotUrl}
                    key={page.path}
                    onClick={onClose}
                    className="menuBurgerGamesInfoWrp"
                    href={page.getPath(params.lang as string)}
                    path={page.path}
                  >
                    <div className="menuBurgerHomeRow">
                      <div
                        onClick={(e) => {
                          if (!!page.childRoutes.length) {
                            selectPage(page, e);
                          }
                        }}
                        className={classNames('menuBurgerHomeRowItem', {
                          active: isGamesPage || page.getPath(currentLang?.link as string) === path,
                        })}
                      >
                        <p className="menuBurgerHomeTitle">{t(page.name)}</p>
                        {!!page.childRoutes.length && (
                          <i className="bc-i-arrow-right menuBurgerArrow" />
                        )}
                      </div>
                    </div>
                  </MobileMenuItemWrapper>
                );
              })}
              <div className="menuBurgerHomeRow">
                <div className="menuBurgerHomeRowItem" onClick={() => setLangPageIsOpen(true)}>
                  <Image alt="" width={30} height={22} src={currentLang?.flag} />
                  <p className="menuBurgerLanguageTitle">{t(currentLang?.name as string)}</p>
                  <i className="bc-i-arrow-right menuBurgerArrow" />
                </div>
              </div>
            </div>
          ) : langPageIsOpen ? (
            <LanguagesPage setLangPageIsOpen={setLangPageIsOpen} onClose={onClose} />
          ) : (
            <SinglePage
              lang={currentLang?.link}
              setSinglePage={setSinglePage}
              onClose={onClose}
              {...singlePage}
            />
          )}
        </div>
      </div>

      <div className="menuBurgerMobBottom">
        <p className="menuBurgerBottomTxt">Follow us on</p>
        <div className="menuBurgerBottomIconWrp">
          {socialIcons.map((social, index) => (
            <div key={`${social.name}${index}`} className="menuBurgerBottomIconInner">
              <a
                key={index}
                target="_blank"
                href={social.link}
                rel="noopener noreferrer"
                aria-label={social.name}
                className="footer-social-link"
              >
                <i className={`${social.logo} menuBurgerIcon`} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MobileHeader;
